import { reducers, getState } from 'redux/core'
import cookie from 'js-cookie'
import request from 'sb-request'
import actions from 'redux/actions'
import { modifyItems } from 'redux/models/queue/api'
import { links, mixpanel, constants, getPrefixedAddressFields } from 'common/helpers'
import { callVindicia, setupBeginPayPal, setupCommitPayPal, setupBeginApplePay,
  setupCommitApplePay, syncUserAndSubscription, callAnalytics } from 'redux/models/payment/util'

import queueInfoQuery from './queueInfo.graphql'
import availableProductsQuery from './availableProducts.graphql'


const getSelectedProductIds = () => {
  const { selectedQueueIds } = cookie.getJSON(constants.cookieNames.buyQueuePageData) || {}

  return selectedQueueIds
}

const getAnalyticsData = () => {
  const {
    view: {
      redeemQueuePage: { queueProducts },
      paymentPageNew: { bill: { total }, inquire: { data: { discount } } },
    },
  } = getState()

  const products = {
    ids: [],
    items: {},
  }

  queueProducts.forEach((product) => {
    const { id } = product

    products.ids.push(id)
    products.items[id] = {
      amount: 1,
      ...product,
    }
  })

  return {
    cart: {
      products,
      discount,
      totalPrice: total,
    },
    totalAmount: total,
    count: queueProducts.length,
  }
}

const paymentWrapper = (action) =>
  action
    .then((result) => syncUserAndSubscription({
      paymentType: constants.paymentTypes.ecommerce,
      ...getAnalyticsData(),
      ...result,
    }))
    .then(
      () => {
        cookie.remove(constants.cookieNames.buyQueuePageData)

        actions.models.queue.fetch()

        callAnalytics()

        actions.models.router.push(links.thankYou)

        mixpanel.track(constants.analyticsEvents.paymentForm.placeOrder, {
          success: true,
        })
      },
      (err) => {
        const { title, message, statusCode, resData: { isCvvError, authCode } = {} } = err

        if (isCvvError) {
          actions.models.modals.open(constants.modals.ErrorCVV)
        }
        else {
          actions.models.modals.open(constants.modals.ServerError, {
            title,
            content: message,
            statusCode,
          })
        }

        mixpanel.track(constants.analyticsEvents.paymentForm.placeOrder, {
          success: false,
          reason: `${authCode} | ${title} | ${message}`,
        })

        return Promise.reject(err)
      }
    )

const fetchCanBuyInOneClickStatus = () =>
  request.get('rest/order/fast')
    .then(({ canBuyInOneClick } = {}) => {
      reducers.view.redeemQueuePage.setCanBuyInOneClickStatus(canBuyInOneClick)
    })

const buyInOneClick = () => {

  const action = request.post('rest/order/fast', {
    body: {
      queueItemIds: getSelectedProductIds(),
    },
    tokenName: constants.requestTokens.order,
  })

  return paymentWrapper(action)
}


// ------------------------------------------------------------------------------ /

const fetchAvailableForQueueProducts = (queueProducts) => {
  const productIds = queueProducts.map(({ id }) => id)

  return request.post('graphql?name=buyQueueAvailableProducts', {
    body: {
      query: availableProductsQuery,
      variables: {
        input: {
          includeProducts: productIds,
          availableForQueue: true,
        },
      },
    },
    modifyResult: ({ products }) => products.map(({ id }) => id),
  })
    .then((availableProductIds) => (
      queueProducts.filter(({ id }) => availableProductIds.includes(id))
    ))
}


const fetchQueue = () => {
  const { models: { subscription: { currPlan } } } = getState()
  const { billingPeriod, shippingPeriod, productsPerPeriod } = currPlan

  const plansQueryInput = {
    products: productsPerPeriod,
    shipping: shippingPeriod,
    billing: billingPeriod,
  }

  reducers.view.redeemQueuePage.setQueueFetchingStatus(true)

  return request.post('graphql?name=buyQueueInfo', {
    body: {
      query: queueInfoQuery,
      variables: {
        plansQueryInput,
      },
    },
    modifyResult: ({ queue, plansQuery }) => ({
      ...modifyItems(queue),
      planPrice: (plansQuery && plansQuery[0]) ? plansQuery[0].productPrice : 0,
    }),
  })
    .then(({ planPrice, items }) => {
      const products = items.map(({ products }) => (
        products
          .filter(({ queueItemId, isUnavailable, isEmpty, disabled }) => (
            queueItemId && !disabled && !isUnavailable && !isEmpty
          ))
      ))

      const flatProducts = [].concat(...products).map((item) => ({
        ...item,
        price: planPrice,
      }))

      return fetchAvailableForQueueProducts(flatProducts)
    })
    .then((products) => {
      reducers.view.redeemQueuePage.setQueueProducts(products)
      return products
    })
    .finally(() => {
      reducers.view.redeemQueuePage.setQueueFetchingStatus(false)
    })
    .catch((err) => {
      console.error(err)
      return Promise.reject(err)
    })
}

const fetchInquire = (values) => (
  request.get('rest/order/inquire', {
    query: values,
    multiple: true,
    withErrorModal: false,
    withHumbleErrorModal: false,
  })
)

/**
 *
 * @param {Object} values
 * @param {array} values.queueItemIds
 */
const inquire = (values) => {
  reducers.view.redeemQueuePage.setInquireFetchingStatus(true)

  return fetchInquire(values)
    .then(({ price: total, ...rest }) => {
      reducers.view.redeemQueuePage.setInquireData({ total, ...rest })
    }, () => {
      reducers.view.redeemQueuePage.setInquireFetchingStatus(false)
    })
}


// ------------------------------------------------------------------------------ /


const begin = (values) => {
  const { view: { redeemQueuePage: { useCredits } } } = getState()
  const { shipping, billing, paymentMethod } = values

  const body = {
    ...getPrefixedAddressFields({ shipping, billing }),
    paymentType: paymentMethod,
    queueItemIds: getSelectedProductIds(),
    ignoreCredits: !useCredits,
  }

  return request.post('rest/order/begin', {
    body,
    tokenName: constants.requestTokens.order,
  })
}

const commit = (values) => {
  const body = {
    ...values,
    ignoreCredits: true,
  }

  return request.post('rest/order/commit', {
    body,
    tokenName: constants.requestTokens.order,
  })
}

const redeem = (values) => {
  const { view: { paymentPageNew: { inquire: { data } } } } = getState()
  const { shipping, billing, creditCard, paymentType } = values

  const withVindicia = data.price > 0

  const action = begin(values)
    .then((data) => withVindicia
      ? callVindicia({
        paymentType,
        shipping,
        billing,
        creditCard,
        ...data,
      })
      : data
    )
    .then(({ orderSession, token }) => commit({ orderSession, token }))

  return paymentWrapper(action)
}


// PayPal

const beginRedeemUsingPayPal = (values) => {
  const beginPayPal = setupBeginPayPal({
    begin,
    sessionName: 'orderSession',
  })

  return beginPayPal(values)
}

/**
 *
 * @param values
 * @param {string} values.paymentType
 * @param {string} values.paypalSessionId
 */
const commitRedeemUsingPayPal = (values) => {
  const commitPayPal = setupCommitPayPal({
    commit,
    sessionName: 'orderSession',
  })

  return commitPayPal(values)
    .then(
      () => {
        callAnalytics()
        actions.models.router.push(links.thankYou)
      },
      (err) => Promise.reject(err)
    )
}


// ApplePay

const beginRedeemUsingApplePay = (values) => {
  const beginApplePay = setupBeginApplePay({
    begin,
    sessionName: 'orderSession',
  })

  return beginApplePay(values)
}

const commitRedeemUsingApplePay = (session, values) => {
  const commitApplePay = setupCommitApplePay({
    commit,
    session,
    sessionName: 'orderSession',
    paymentType: constants.paymentTypes.ecommerce,
  })

  return commitApplePay(values)
    .then(
      () => {
        callAnalytics()
        actions.models.router.push(links.thankYou)
      },
      (err) => Promise.reject(err)
    )
}

const { setUseCredits } = reducers.view.redeemQueuePage


export default {
  fetchCanBuyInOneClickStatus,
  fetchQueue,
  fetchInquire,
  inquire,
  redeem,
  buyInOneClick,
  setUseCredits,
  beginRedeemUsingPayPal,
  commitRedeemUsingPayPal,
  beginRedeemUsingApplePay,
  commitRedeemUsingApplePay,
}
